import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { getStoredSessionId, setStoredSessionId } from './api';

export const API_BASE = import.meta.env.VITE_API_BASE || 'https://dietamenu.schedy.app';

export type ProKind = 'nutri' | 'coach' | 'both';

export interface AuthUser {
  id: string;
  name: string;
  email: string | null;
  role: 'patient' | 'dietologist';
  pro_code: string | null;
  human_id: string | null;
  is_admin: 0 | 1;
  subscription_status: string | null;
  /** Sprint 6: figura del professionista. null/undefined → assumi 'nutri' (legacy) */
  pro_kind?: ProKind | null;
  studio_name?: string | null;
}

export type AuthState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | { status: 'authenticated'; user: AuthUser }
  | { status: 'error'; message: string };

interface AuthContextValue {
  state: AuthState;
  user: AuthUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  const refresh = async () => {
    setState({ status: 'loading' });
    const url = `${API_BASE}/api/auth/me`;
    try {
      const sid = getStoredSessionId();
      const headers: Record<string, string> = {};
      if (sid) headers['X-Session-Id'] = sid;
      // eslint-disable-next-line no-console
      console.log('[auth] fetch', url, '· origin:', window.location.origin, '· sid-hdr:', !!sid);
      const res = await fetch(url, { credentials: 'include', headers });
      // eslint-disable-next-line no-console
      console.log('[auth] response', res.status, '· cors-origin:', res.headers.get('access-control-allow-origin'));

      if (!res.ok) {
        setState({ status: 'error', message: `HTTP ${res.status} su /api/auth/me` });
        return;
      }
      const data = await res.json() as { user: AuthUser | null };
      // eslint-disable-next-line no-console
      console.log('[auth] user:', data.user ? `${data.user.name} (${data.user.role})` : 'null');

      if (data.user) {
        setState({ status: 'authenticated', user: data.user });
      } else {
        setStoredSessionId(null); // session stale, pulisci localStorage
        setState({ status: 'unauthenticated' });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      // eslint-disable-next-line no-console
      console.error('[auth] fetch failed:', msg);
      setState({ status: 'error', message: `Network: ${msg}` });
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API_BASE}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch {
      // ignore network error on logout
    }
    // Sprint 9.D — pulisci tutta la state lato client per evitare carry-over
    // tra test sessions / accountswitch.
    setStoredSessionId(null);
    try {
      window.localStorage.removeItem('dietamenu.pro.onboarded');
      window.localStorage.removeItem('dietamenu-piano-editor-draft-v1');
      window.localStorage.removeItem('dietamenu-piano-templates-v1');
      window.localStorage.removeItem('admin.section');
    } catch {
      // localStorage può fallire in incognito strict
    }
    setState({ status: 'unauthenticated' });
    // Atterra su /login del portale pro (non su SPA paziente — UX coerente per pro)
    window.location.href = '/login';
  };

  useEffect(() => {
    // Sprint 9.D — handoff session_id via URL query (cross-subdomain register flow).
    // SPA paziente registra pro → redirect pro-web/onboarding?sid=XYZ → qui leggiamo,
    // salviamo in localStorage, puliamo URL, poi refresh(). Bypass Chrome CHIPS che
    // partition i cookie cross-subdomain.
    try {
      const params = new URLSearchParams(window.location.search);
      const urlSid = params.get('sid');
      if (urlSid && /^[a-f0-9-]{36}$/i.test(urlSid)) {
        setStoredSessionId(urlSid);
        params.delete('sid');
        const clean = window.location.pathname + (params.toString() ? '?' + params.toString() : '') + window.location.hash;
        window.history.replaceState({}, '', clean);
      }
    } catch { /* URL parsing / history API mancanti */ }
    refresh();
  }, []);

  const user = state.status === 'authenticated' ? state.user : null;
  const loading = state.status === 'loading';

  return (
    <AuthContext.Provider value={{ state, user, loading, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
